<script setup>
import { defineProps, defineEmits, ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { useLayoutComponents } from '@/composables/layout';
import { useToast } from '@/composables/use-toast';
import { uploadDatasetFile } from '@/services/api';
import Alert from '@/components/Alert.vue';

const { PrimaryButton, TextInput, MultiSelect, FileSelect, ButtonLoading, FilePreviewModal } = useLayoutComponents();
const { addToast } = useToast();

const emit = defineEmits(['close', 'save', 'delete']);
const isLoading = ref(false);

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: 'Add New Item'
    },
    initialData: Object,
    fields: {
        type: Array,
        default: () => []
    },
    availableTags: {
        type: Array,
        default: () => []
    },
    closeOnClickOutside: {
        type: Boolean,
        default: false
    },
    isListing: {
        type: Boolean,
        default: false
    },
    buttonText: {
        type: String,
        default: 'Save'
    },
    buttonLoadingText: {
        type: String,
        default: 'Saving...'
    },
    modalSize: {
        type: String,
        default: 'full'
    },
    buttonDisabled: {
        type: Boolean,
        default: false
    },
    showCancelButton: {
        type: Boolean,
        default: true
    },
    titleIcon: {
        type: String,
        default: ''
    }
});

const showFilePreview = ref(false);
const previewMode = ref('preview');
const selectedFileInfo = ref({
    name: 'example.parquet',
    size: 1024000,
    type: 'parquet',
    columns: [
        { name: 'id', type: 'integer' },
        { name: 'name', type: 'string' },
    ],
    rows: 10000
});

const modalRef = ref(null);
const formData = ref({});
const fileInput = ref(null);

const processedFields = computed(() => {
    return props.fields.map(field => {
        if (field.key === 'title' || field.key === 'description' ||
            field.type === 'file-select' || field.type === 'tags' ||
            field.type === 'image-upload') {
            return { ...field, fullWidth: false };
        }
        return field;
    });
});

const disableBodyScroll = () => {
    document.body.style.overflow = 'hidden';
    document.querySelectorAll('body > *:not([data-modal])').forEach(el => {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
    });
};

const enableBodyScroll = () => {
    document.body.style.overflow = '';
    document.querySelectorAll('[inert]').forEach(el => {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
    });
};

const resetFormData = () => {
   
    const emptyData = {};
    props.fields.forEach(field => {
        if (field.type === 'tags') {
            emptyData[field.key] = [];
        } else if (field.type === 'file-select') {
            emptyData[field.key] = null;
            if (field.modelValue) {
                field.modelValue = null;
            }
        } else if (field.key === 'dataset_id') {
            emptyData[field.key] = '';
            if (field.onChange) {
                field.onChange('', (value) => {
                    emptyData[field.key] = value;
                });
            }
        } else {
            emptyData[field.key] = field.defaultValue || '';
        }
    });

    formData.value = emptyData;
    
  
};

watch(() => props.initialData, (newData) => {
    if (newData) {
        formData.value = { ...newData };
        if (newData.tags) {
            formData.value.listing_tags = newData.tags.map(tag => tag.tag_id || tag.id);
        }
        if (newData.file_name && newData.file_size) {
            formData.value.file = {
                name: newData.file_name,
                size: parseInt(newData.file_size),
                extension: newData.file_extension,
                path: newData.file_name
            };
        }
    } else {
        const emptyData = {};
        props.fields.forEach(field => {
            emptyData[field.key] = field.defaultValue || '';
        });
        emptyData.listing_tags = [];
        formData.value = emptyData;
    }
}, { immediate: true });

watch(() => props.show, (isVisible) => {
    if (isVisible) {
        disableBodyScroll();
        if (props.initialData) {
            formData.value = { ...props.initialData };
        }
    } else {
        enableBodyScroll();
        resetFormData();
        props.fields.forEach(field => {
            if (field.type === 'file-select') {
                field.modelValue = null;
            }
        });
    }
});

watch(() => props.fields, (newFields) => {

    const datasetField = newFields.find(field => field.key === 'dataset_id');
    
    if (datasetField && formData.value?.dataset_id) {
        const selectedDataset = datasetField.options?.find(
            option => option.value === formData.value.dataset_id
        );
        
        if (selectedDataset) {
            datasetField.displayValue = selectedDataset.label;
           
        }
    }
}, { deep: true });

watch(() => props.fields, (newFields) => {

    const fileField = newFields.find(field => field.type === 'dataset-file');
    
    if (fileField) {
    
        if (fileField.modelValue && formData.value) {
            formData.value[fileField.key] = fileField.modelValue;
          
        }
    }
}, { deep: true, immediate: true });




const validateFile = (file) => {
    const allowedExtensions = ['parquet', 'gz'];
    const fileExtension = file.name.split('.').pop();
    return allowedExtensions.includes(fileExtension);
};

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};



const handleSave = async () => {
    isLoading.value = true;
    
    try {
        const requiredFields = props.fields.filter(field => field.required);
        const invalidFields = [];

        for (const field of requiredFields) {
            if (!formData.value[field.key] || formData.value[field.key] === '') {
                invalidFields.push(field.label || field.key);
            }
        }

        const receiptAddressField = props.fields.find(field => field.key === 'receipt_address');
        if (receiptAddressField && formData.value.receipt_address) {
            const walletRegex = /^0x[a-fA-F0-9]{40}$/;
            if (!walletRegex.test(formData.value.receipt_address)) {
                addToast('Invalid wallet address format', 'error');
                return;
            }
        }
        
        const payoutAddressField = props.fields.find(field => field.key === 'payout_address');
        if (payoutAddressField && formData.value.payout_address) {
            const walletRegex = /^0x[a-fA-F0-9]{40}$/;
            if (!walletRegex.test(formData.value.payout_address)) {
                addToast('Invalid wallet address format. Must be 42 characters starting with 0x', 'error');
                return;
            }
        }

        if (invalidFields.length > 0) {
            addToast(`Please fill in the required fields: ${invalidFields.join(', ')}`, 'warning');
            return;
        }

       
        const saveResult = await emit('save', { ...formData.value });
        
           
        if (saveResult !== false) {
            resetFormData();
            emit('close');
        }
    } catch (error) {
        console.error('Error in handleSave:', error);
        addToast('Error saving data', 'error');
    } finally {
        isLoading.value = false;
    }
};

const handleClose = () => {
   
    resetFormData();
    emit('close');
};

const handleClickOutside = (event) => {
    if (props.closeOnClickOutside && modalRef.value && !modalRef.value.contains(event.target)) {
        handleClose();
    }
};

const handleImageUpload = (event, fieldKey) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
        formData.value[fieldKey] = e.target.result;
    };
    reader.readAsDataURL(file);
};

const handleFileUpload = async (event, fieldKey) => {
    const file = event.target.files[0];

    if (fileInput.value) {
        fileInput.value.value = '';
    }
    
    if (!file) return;

    if (!validateFile(file)) {
        addToast('Invalid file format', 'error');
        return;
    }

    const uploadFile = async (fileToUpload, customName = null) => {
        try {
            isLoading.value = true;
            formData.value[`${fieldKey}_progress`] = 0;
            
            const response = await uploadDatasetFile(
                fileToUpload, 
                (progress) => {
                    const normalizedProgress = Math.min(Math.max(0, Number(progress)), 100);
                    formData.value[`${fieldKey}_progress`] = normalizedProgress;
                },
                customName
            );
                
            if (!response) {
                throw new Error('Invalid response from server');
            }

            if (response.error === 'FILE_EXISTS') {
                throw new Error('FILE_EXISTS');
            }

            formData.value[fieldKey] = {
                name: customName || fileToUpload.name,
                size: fileToUpload.size,
                extension: (customName || fileToUpload.name).split('.').pop(),
            };

            if (response.previewData) {
                formData.value[fieldKey].schema = response.previewData.schema;
                formData.value[fieldKey].columns = response.previewData.columns;
                formData.value[fieldKey].rows = response.previewData.rows;
                
                selectedFileInfo.value = {
                    name: formData.value[fieldKey].name,
                    size: formatFileSize(formData.value[fieldKey].size),
                    type: formData.value[fieldKey].extension,
                    columns: response.previewData.columns || [],
                    rows: response.previewData.rows || 0,
                    schema: response.previewData.schema || null
                };
            }

            formData.value[`${fieldKey}_progress`] = 100;
            addToast('File uploaded successfully!', 'success');

        } catch (error) {
            if (error.message === 'FILE_EXISTS') {
                const fileName = fileToUpload.name;
                const firstDotIndex = fileName.indexOf('.');
                const baseName = fileName.slice(0, firstDotIndex);
                const extension = fileName.slice(firstDotIndex + 1);
                const defaultNewName = `${baseName}.${extension}`;

                return new Promise((resolve) => {
                    showFileNameAlert.value = true;
                    fileNameAlertData.value = {
                        originalName: fileName,
                        defaultNewName,
                        resolve: async (newName) => {
                            showFileNameAlert.value = false;
                            if (!newName) {
                                addToast('Upload cancelled', 'info');
                                resolve();
                                return;
                            }

                            try {
                                await uploadFile(fileToUpload, newName);
                                resolve();
                            } catch (uploadError) {
                                if (uploadError.message === 'FILE_EXISTS') {
                                    addToast('File with this name already exists. Please try a different name.', 'error');
                                    await handleFileUpload({ target: { files: [fileToUpload] } }, fieldKey);
                                    resolve();
                                } else {
                                    throw uploadError;
                                }
                            }
                        }
                    };
                });
            } else {
                console.error('Error uploading file:', error);
                addToast(`Error uploading file: ${error.message}`, 'error');
                formData.value[fieldKey] = null;
                formData.value[`${fieldKey}_progress`] = 0;
            }
        } finally {
            isLoading.value = false;
        }
    };

    await uploadFile(file);
};

const handleSchemaUpdate = (updatedSchema) => {
    if (updatedSchema) {
    
        const currentField = props.fields.find(field => field.type === 'dataset-file');
        if (currentField && formData.value[currentField.key]) {
            formData.value[currentField.key].schema = updatedSchema.schema;
            formData.value[currentField.key].columns = updatedSchema.columns;
            
            // update preview data
            selectedFileInfo.value = {
                ...selectedFileInfo.value,
                schema: updatedSchema.schema,
                columns: updatedSchema.columns
            };
        }
        addToast('Schema updated successfully', 'success');
    }
    showFilePreview.value = false;
};

const showDeleteAlert = ref(false);

const handleDelete = () => {
    showDeleteAlert.value = true;
};

const handleDeleteConfirm = () => {
    emit('delete', formData.value);
    showDeleteAlert.value = false;
};

const showFileNameAlert = ref(false);
const fileNameAlertData = ref(null);

const handleFileNameAlertClose = () => {
    showFileNameAlert.value = false;
    if (fileNameAlertData.value?.resolve) {
        fileNameAlertData.value.resolve(null);
    }
    fileNameAlertData.value = null;
    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const handleFileNameAlertConfirm = (newFileName) => {
    showFileNameAlert.value = false;
    if (fileNameAlertData.value?.resolve) {
        fileNameAlertData.value.resolve(newFileName);
    }
    fileNameAlertData.value = null;
};

onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside);
    if (props.show) {
        disableBodyScroll();
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    enableBodyScroll();
});

const modalSizeClass = computed(() => {
    const sizeClasses = {
        'large': 'w-full',
        'medium': 'w-1/2',
        'small': 'w-1/3',
        'normal': 'w-2/5'
    }
    return sizeClasses[props.modalSize] || 'w-full';
});
</script>

<template>
    <Teleport to="body">
        <div v-if="show" class="modal-overlay" data-modal>
            <div ref="modalRef" class="modal" :class="modalSizeClass">
                <div class="modal__header">
                    <h2 class="modal__title"> <div v-if="props.titleIcon" v-html="props.titleIcon"></div> {{ title }}</h2>
                    <button class="modal__close" @click="handleClose">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="modal__description">
                    <slot name="description"></slot>
                </div>
                <div class="modal__body">
                    <slot v-if="$slots.default">
                        <!-- Default slot content -->
                        <slot></slot>
                    </slot>
                    <div v-else class="modal__form">
                        <div v-for="field in processedFields" :key="field.key" class="form-group" :class="{
                            'form-group--full': field.fullWidth === true,
                            'form-group--half': field.fullWidth !== true,
                            'form-group--required': field.required
                        }">
                            <label :for="field.key" class="form-label">
                                {{ field.label || field.key }}
                                <span v-if="field.required" class="form-label__required">*</span>
                            </label>
                            <template v-if="field.type === 'tags'">
                                <MultiSelect v-model="formData.listing_tags" :options="availableTags"
                                    option-label="name" option-value="id"
                                    :placeholder="field.placeholder || 'Select tags'" :multiple="true" />
                            </template>
                            <template v-else-if="field.type === 'file-select'">
                                <FileSelect 
                                    v-model="formData[field.key]"
                                    :placeholder="formData.file_name || field.placeholder || 'Select a file'" 
                                    :fileList="field.fileList || []" 
                                    :useApi="field.useApi !== false"
                                    :isListing="props.isListing"
                                    :displayMode="field.displayMode || false"
                                    :key="`file-select-${field.key}-${JSON.stringify(formData[field.key])}`"
                                />
                            </template>
                            <template v-else-if="field.type === 'image-upload'">
                                <div class="image-upload-container">
                                    <input type="file" :id="field.key" accept="image/*"
                                        @change="(e) => handleImageUpload(e, field.key)" class="image-upload-input" />
                                    <label :for="field.key" class="image-upload-label">
                                        <div class="flex items-center justify-between w-full">
                                            <span v-if="!formData[field.key]">Choose an image</span>
                                            <span v-else class="truncate">Image selected</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24">
                                                <path stroke="currentColor" stroke-linecap="round"
                                                    stroke-linejoin="round" stroke-width="2"
                                                    d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2M12 4v12m0-12 4 4m-4-4L8 8" />
                                            </svg>

                                        </div>
                                    </label>
                                    <div v-if="formData[field.key]" class="image-preview">
                                        <img :src="formData[field.key]" alt="Preview" class="preview-img" />
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="field.type === 'static'">
                                <div class="form-static-text">{{ field.value }}</div>
                            </template>
                            <template v-else-if="field.type === 'dataset-file'">
                                <div class="file-upload-container">
                                    <input 
                                        type="file" 
                                        :id="field.key"
                                        ref="fileInput"
                                        :accept="field.accept"
                                        @change="(e) => handleFileUpload(e, field.key)"
                                        @click="(e) => e.target.value = ''"
                                        class="file-upload-input"
                                        :disabled="isLoading"
                                    />
                                    <label :for="field.key" class="file-upload-label" :class="{ 'opacity-50': isLoading }">
                                        <div class="flex items-center justify-between w-full">
                                            <span v-if="!formData.file">Choose a dataset file</span>
                                            <span v-else class="truncate">{{ formData.file.name }}</span>
                                            <svg v-if="!isLoading" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2M12 4v12m0-12 4 4m-4-4L8 8" />
                                            </svg>
                                            <div v-else class="loading-spinner-small"></div>
                                        </div>
                                    </label>
                                 
                                    <div v-if="formData.file" class="file-info">
                                        <p class="file-name">{{ formData.file.name }}</p>
                                        <p class="file-size">{{ formatFileSize(formData.file.size) }}</p>
                                        <div class="flex gap-2" v-if="formData.file.schema || formData.file.columns">
                                            <button 
                                                @click="() => { previewMode = 'preview'; showFilePreview = true; }"
                                                class="preview-button"
                                            >
                                                Preview Schema
                                            </button>
                                            <button 
                                                @click="() => { previewMode = 'edit'; showFilePreview = true; }"
                                                class="edit-button"
                                            >
                                                Edit Schema
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="formData[`${field.key}_progress`] !== undefined" class="progress-bar-container">
                                        <div 
                                            class="progress-bar" 
                                            :style="{ width: `${formData[`${field.key}_progress`]}%` }"
                                        ></div>
                                        <span class="progress-text">{{ Math.round(formData[`${field.key}_progress`]) }}%</span>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <slot v-if="field.type === 'select'" name="field" :field="field"
                                    :model-value="formData[field.key]"
                                    :update-model-value="(value) => formData[field.key] = value">
                                </slot>
                                <TextInput v-else :id="field.key" v-model="formData[field.key]"
                                    :placeholder="field.placeholder || ''" :type="field.type || 'text'"
                                    :required="field.required"
                                    :pattern="field.key === 'receipt_address' ? '^0x[a-fA-F0-9]{40}$' : ''" />
                            </template>
                            <p v-if="field.hint" class="form-hint">{{ field.hint }}</p>
                        </div>
                    </div>
                </div>
                <div class="modal__footer">
                    <div class="modal__footer-left">
                        <button 
                            v-if="initialData && !isListing && title.toLowerCase().includes('edit')" 
                            class="btn btn--danger" 
                            @click="handleDelete" 
                            :disabled="isLoading"
                        >
                            Delete
                        </button>
                    </div>
                    <div class="modal__footer-right">
                        <button class="btn btn--secondary" @click="handleClose" :disabled="isLoading" v-if="showCancelButton">Cancel</button>
                        <PrimaryButton @click="handleSave" :loading="isLoading" :disabled="buttonDisabled">
                            <ButtonLoading v-if="isLoading" size="sm" :text="buttonLoadingText" />
                            <template v-else>{{ buttonText }}</template>
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
        <FilePreviewModal 
            :show="showFilePreview" 
            :fileInfo="selectedFileInfo"
            :mode="previewMode"
            @close="showFilePreview = false"
            @save="handleSchemaUpdate"
        />
        <Alert
            :show="showFileNameAlert"
            title="File Already Exists"
            message="A file with this name already exists. Please enter a new name for the file:"
            type="warning"
            confirmText="Rename"
            :showInput="true"
            :inputLabel="'New File Name'"
            :inputRequired="true"
            :defaultValue="fileNameAlertData?.defaultNewName"
            @close="handleFileNameAlertClose"
            @confirm="handleFileNameAlertConfirm"
        />
        <Alert
            :show="showDeleteAlert"
            title="Delete Dataset"
            message="Are you sure you want to delete this dataset? This action cannot be undone."
            type="danger"
            confirmText="Delete"
            @close="showDeleteAlert = false"
            @confirm="handleDeleteConfirm"
        />
    </Teleport>
</template>

<style scoped lang="postcss">
.modal-overlay {
    @apply fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-2;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.modal {
    @apply bg-echoes-background-gray border border-echoes-border-gray/50 rounded-xl shadow-xl max-h-[85vh] mt-16 flex flex-col;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;

    @screen sm {
        max-width: 540px;
    }
    @screen md {
        max-width: 720px;
    }
    @screen lg {
        max-width: 960px;
    }
}

.modal__header {
    @apply sticky top-0 z-10 flex items-center justify-between p-4 border-b border-echoes-border-gray bg-echoes-background-gray rounded-t-xl;
}

.modal__title {
    @apply text-xl font-semibold text-white flex items-center gap-2;
}

.modal__description {
    @apply px-4 bg-echoes-background-gray rounded-b-xl text-sm text-white/60;
}

.modal__close {
    @apply text-white/80 hover:text-red-500/80 p-1 rounded-full transition-colors;
}

.modal__body {
    @apply p-4 bg-echoes-background-gray rounded-b-xl;
    flex: 1;
}

.modal__form {
    @apply grid gap-4 sm:gap-6;
    grid-template-columns: 1fr;

    @screen sm {
        grid-template-columns: repeat(2, 1fr);
    }
}

.form-group {
    @apply flex flex-col gap-1.5;
}

.form-group--full {
    @apply col-span-1 sm:col-span-2;
}

.form-group--half {
    @apply sm:col-span-1;
}

.form-group--required {
    @apply border-red-300 dark:border-red-500/50;
}

.form-label {
    @apply text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1;
}

.form-label__required {
    @apply text-red-500 ml-0.5;
}

.form-hint {
    @apply text-xs text-gray-500 dark:text-gray-400 mt-1;
}

.modal__footer {
    @apply sticky bottom-0 z-10 p-4 border-t border-echoes-border-gray flex items-center justify-between gap-3 bg-echoes-background-gray rounded-b-xl;
}

.modal__footer-left {
    @apply flex items-center;
}

.modal__footer-right {
    @apply flex items-center gap-3;
}

.btn {
    @apply px-4 py-2 rounded-lg font-medium transition-colors;
}

.btn--secondary {
    @apply px-4 py-1.5 2xl:px-6 2xl:py-2.5 rounded-lg text-xs 2xl:text-sm font-medium transition-colors dark:hover:border-echoes-primary dark:hover:text-echoes-primary dark:bg-white/10 dark:text-white dark:border-white/20 hover:border-echoes-primary/30 hover:text-echoes-primary/60 bg-white text-gray-700 border border-gray-200;
}

.btn--danger {
    @apply px-4 py-1.5 rounded-lg text-sm font-medium transition-colors 
    bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200/80 dark:border-red-700/80
    hover:bg-red-100 dark:hover:bg-red-900/50 hover:border-red-300 dark:hover:border-red-600;
}

.form-static-text {
    @apply w-fit px-4 py-3 text-sm rounded-2xl bg-gray-200 dark:bg-gray-900/80 text-gray-900 dark:text-gray-100 border border-gray-200/80 dark:border-gray-700/80 placeholder-gray-500/70 dark:placeholder-gray-400/70 transition-all duration-200 ease-in-out;

    &:hover {
        @apply bg-gray-100 dark:bg-gray-800/90 border-gray-300 dark:border-gray-600 shadow-sm;
    }

    &:focus {
        @apply outline-none border-echoes-primary ring-2 ring-echoes-primary/30 bg-echoes-background-black shadow-md shadow-echoes-primary/10;
    }

    &::placeholder {
        @apply transition-all duration-200;
    }

    &:focus::placeholder {
        @apply opacity-0 translate-x-1;
    }
}

.image-upload-container {
    @apply w-full;
}

.image-upload-input {
    @apply hidden;
}

.image-upload-label {
    @apply block w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900/80 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-700/80 rounded-2xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/90 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-colors;
}

.image-preview {
    @apply mt-2 relative w-full border border-gray-300 dark:border-gray-700 rounded-2xl overflow-hidden;
    max-height: 200px;
}

.preview-img {
    @apply w-full h-auto object-contain;
    max-height: 200px;
}

.file-upload-container {
    @apply w-full;
}

.file-upload-input {
    @apply hidden;
}

.file-upload-label {
    @apply block w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900/80 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-700/80 rounded-2xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/90 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-colors;
}

.file-info {
    @apply mt-2 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg;
}

.file-name {
    @apply text-sm font-medium text-gray-700 dark:text-gray-300;
}

.file-size {
    @apply text-xs text-gray-500 dark:text-gray-400;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.form-group--required .form-label {
    @apply font-medium;
}

.form-group--required input:invalid,
.form-group--required select:invalid {
    @apply border-red-300 dark:border-red-500/50;
}

.loading-spinner-small {
    @apply w-4 h-4 border-2 border-echoes-border-gray border-t-echoes-primary rounded-full animate-spin;
}

.dark .loading-spinner-small {
    @apply border-echoes-border-gray border-t-echoes-primary;
}
.progress-bar-container {
    @apply relative w-full h-2 bg-echoes-border-gray rounded-full overflow-hidden mt-2;
}

.progress-bar {
    @apply h-full bg-echoes-primary transition-all duration-300 ease-in-out;
}

.progress-text {
    @apply absolute top-2 left-1/2 transform -translate-x-1/2 text-xs text-white;
}

.preview-button, .edit-button {
    @apply mt-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors;
}

.preview-button {
    @apply text-echoes-primary bg-echoes-background-black 
    hover:bg-echoes-background-black/80;
}

.edit-button {
    @apply text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 
    hover:bg-emerald-100 dark:hover:bg-emerald-900/50;
}
</style>